/**
 * Lead Deduplication Utility
 * Groups leads by phone number to prevent duplicate entries
 */

import { logInfo, logError } from "@/shared/utils/logger.js";

/**
 * Normalize phone number for comparison
 * @param {string} phone - Phone number
 * @returns {string} Normalized phone number
 */
const normalizePhone = (phone) => {
  if (!phone) return null;
  
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, "");
  
  // Normalize to international format (27XXXXXXXXX)
  if (digitsOnly.startsWith("27")) {
    return digitsOnly;
  } else if (digitsOnly.startsWith("0")) {
    return "27" + digitsOnly.substring(1);
  } else if (digitsOnly.length >= 9) {
    // Assume it's a local number, add 27 prefix
    return "27" + digitsOnly;
  }
  
  return digitsOnly;
};

/**
 * Group leads by phone number
 * @param {Array} leads - Array of lead objects
 * @returns {Object} Object with grouped and ungrouped leads
 * 
 * @example
 * const { grouped, ungrouped } = groupLeadsByPhone(leads);
 * // grouped: [{ phone, primaryLead, allLeads, count }]
 * // ungrouped: [leads without phone numbers]
 */
export const groupLeadsByPhone = (leads = []) => {
  if (!Array.isArray(leads) || leads.length === 0) {
    return {
      grouped: [],
      ungrouped: [],
    };
  }

  try {
    const phoneMap = new Map();
    const ungrouped = [];

    // Group leads by normalized phone number
    leads.forEach((lead) => {
      const phone = lead.phone;
      
      if (!phone) {
        // Leads without phone numbers go to ungrouped
        ungrouped.push(lead);
        return;
      }

      const normalizedPhone = normalizePhone(phone);
      
      if (!normalizedPhone) {
        ungrouped.push(lead);
        return;
      }

      if (!phoneMap.has(normalizedPhone)) {
        phoneMap.set(normalizedPhone, []);
      }
      
      phoneMap.get(normalizedPhone).push(lead);
    });

    // Convert map to grouped array
    const grouped = Array.from(phoneMap.entries()).map(([phone, leadGroup]) => {
      // Sort by createdAt (most recent first)
      const sortedLeads = leadGroup.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.created_at || 0);
        const dateB = new Date(b.createdAt || b.created_at || 0);
        return dateB - dateA;
      });

      return {
        phone,
        primaryLead: sortedLeads[0], // Most recent lead
        allLeads: sortedLeads,
        count: sortedLeads.length,
      };
    });

    logInfo("Leads grouped by phone", {
      totalLeads: leads.length,
      groupedCount: grouped.length,
      ungroupedCount: ungrouped.length,
    });

    return {
      grouped,
      ungrouped,
    };
  } catch (error) {
    logError("Failed to group leads by phone", {
      error: error.message,
      stack: error.stack,
    });
    
    // Return all leads as ungrouped on error
    return {
      grouped: [],
      ungrouped: leads,
    };
  }
};

/**
 * Flatten grouped leads for display
 * Returns array suitable for LeadCardList component
 * @param {Object} groupedData - Result from groupLeadsByPhone
 * @returns {Array} Flattened array with grouped leads (with count) and ungrouped leads
 */
export const flattenGroupedLeads = (groupedData) => {
  if (!groupedData || (!groupedData.grouped && !groupedData.ungrouped)) {
    return [];
  }

  const { grouped = [], ungrouped = [] } = groupedData;
  
  // Return grouped leads (with count property) + ungrouped leads
  return [...grouped, ...ungrouped];
};

